import React, { Component } from "react";
import styled from "styled-components";
import { app } from "js/namespaces";
import { FeatureType } from "common/features";
import { DialogTitle, DialogActions, Button, Icon } from "@material-ui/core";

import { _ } from "js/vendor";
import { ds } from "js/core/models/dataService";
import { Theme } from "js/core/models/theme";
import { PREDEFINED_PALETTES } from "common/constants";
import { UIController } from "js/editor/dialogs/UIController";
import AppController from "js/core/AppController";

import { BeautifulDialog, DialogContent, ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { ThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";

import "css/style-guide.scss";
import { EditThemePanel } from "js/editor/editorPanels/editThemePanel";
import importPPTTheme from "js/core/utilities/importPPTTheme";
import { ThemeThumbnailList } from "js/editor/ThemeEditor/components/ThemeThumbnailList";
import { BlueButton } from "js/react/components/UiComponents";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";

const Subtitle = styled.div`
  margin-top: 10px;

  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 125%;
  letter-spacing: 0.5px;
  color: #222222;
`;

export class ChooseThemeDialog extends Component {
    static defaultProps = {
        title: "Select Theme",
        subtitle: null,
        selectButtonLabel: "Select Theme",
        preventClose: false,
        hideUserThemes: false,
        hideBuiltInThemes: false,
        showCancelButton: true,
        closeDialogOnThemeChoose: true,
        callback: null
    }

    state = {
        selectedTheme: null,
        teamThemes: [],
        userThemes: [],
        pptTheme: null
    }

    componentDidMount() {
        Promise.all([
            this.loadPPTTheme()
        ])
            .then(() => {
                const { hideBuiltInThemes } = this.props;
                const { teamThemes, userThemes, pptTheme } = this.state;
                // Auto select ppt theme if there are no other options
                if (pptTheme && hideBuiltInThemes && teamThemes.length === 0 && userThemes.length === 0) {
                    this.handleChooseTheme(pptTheme);
                }
            });
    }

    setStateAsync(stateUpdates) {
        return new Promise(resolve => this.setState(stateUpdates, resolve));
    }

    async loadPPTTheme() {
        const { pptThemeModel } = this.props;
        if (pptThemeModel) {
            await this.setStateAsync({ pptTheme: new Theme(pptThemeModel, { disconnected: true, autoSync: false }) });
        }
    }

    handleSelectTheme = theme => {
        this.setState({ selectedTheme: theme });
    }

    chooseTheme = theme => {
        this.handleSelectTheme();

        const { callback, closeDialog, closeDialogOnThemeChoose } = this.props;

        if (!theme.has("colors")) {
            const defaultThemePalette = _.find(PREDEFINED_PALETTES, { id: theme.get("palette_name") || "colorful" });
            theme.set("colors", defaultThemePalette.colors);
        }

        if (callback) {
            callback(theme);
        }

        if (closeDialogOnThemeChoose) {
            closeDialog();
        }
    };

    handleChooseTheme = async theme => {
        if (theme === this.state.pptTheme) {
            await theme.loadTheme();
            const editDialog = new EditThemePanel({
                theme,
                isNewTheme: true,
                hideImportTheme: true,
                onSuccess: theme => {
                    this.chooseTheme(theme);
                }
            });
            editDialog.show();
        } else {
            this.chooseTheme(theme);
        }
    }

    handleEditCurrentTheme = () => {
        this.props.closeDialog();
        AppController.showThemeEditor({ themeOrPresentationId: PresentationEditorController.presentation.id });
    }

    handleDeleteTheme = theme => {
        ShowConfirmationDialog({
            title: "Are you sure you want to delete this theme?",
            message: "No existing presentations will be affected by this change but you won't be able to use this theme in any future presentations."
        }).then(confirmed => {
            if (confirmed) {
                theme.collection.remove([theme]);
                theme.destroy();
                this.loadUserThemes();
            }
        });
    }

    render() {
        const {
            selectedTheme,
            teamThemes,
            userThemes,
            pptTheme
        } = this.state;
        const {
            closeDialog,
            title,
            subtitle,
            selectButtonLabel,
            preventClose,
            hideUserThemes,
            hideBuiltInThemes,
            hidePPTImport,
            showCancelButton,
            styles,
            showEditCurrentTheme = true
        } = this.props;

        const teamThemesToRender = teamThemes
            .sort((a, b) => a.id.localeCompare(b.id)); // lexicographic sort

        return (
            <BeautifulDialog
                maxWidth="lg"
                preventClose={preventClose}
                closeDialog={closeDialog}
            >
                <DialogTitle>
                    <FlexBox left>{title}
                        <FlexSpacer/>
                        {showEditCurrentTheme &&
                        <Button onClick={this.handleEditCurrentTheme}>
                            <Icon color="primary">edit</Icon>
                            Edit Current Theme...
                        </Button>
                        }
                    </FlexBox>
                    {subtitle && <Subtitle>{subtitle}</Subtitle>}
                </DialogTitle>
                <DialogContent style={{ ...styles, background: "#f1f1f1" }}>
                    <Gap10/>
                    <ThemeThumbnailList
                        pptTheme={pptTheme}
                        selectedTheme={selectedTheme}
                        onClick={this.handleSelectTheme}
                        onDoubleClick={this.handleChooseTheme}
                    />
                </DialogContent>
                <DialogActions>
                    <FlexBox fillWidth>
                        <FlexSpacer />
                        {showCancelButton && <Button onClick={closeDialog}>Cancel</Button>}
                        <Gap20/>
                        <BlueButton
                            color="primary"
                            onClick={() => this.handleChooseTheme(selectedTheme)}
                            disabled={!selectedTheme}
                        >
                            {selectButtonLabel}
                        </BlueButton>
                    </FlexBox>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}
